import { ReactNode, useMemo } from "react";
import { LoadingStatus } from "src/libs/fetcher";
import { useCardThemed } from "./useCardThemed";
import { Button, ButtonProps } from "@memento/ui/Button";
import {
  getCardThemeButtonColors,
  CardThemeIdentifier,
} from "./card-theme-utils";
import { cn } from "src/utils/cn";

type ThemeButtonVariant = "primary" | "secondary";

const stylesCommon = `border`;

export type ThemeButtonProps = Omit<ButtonProps, "variant"> & {
  themeId?: CardThemeIdentifier;
  variant?: ThemeButtonVariant;
  children: ReactNode;
};

export function ThemeButton({
  themeId,
  variant = "primary",
  children,
  disabled = false,
  ...rest
}: ThemeButtonProps) {
  const { theme, status } = useCardThemed(themeId);

  const colors = useMemo(() => {
    if (variant === "primary") {
      return getCardThemeButtonColors(theme.buttonPrimary);
    } else {
      return getCardThemeButtonColors(theme.buttonSecondary);
    }
  }, [variant, theme]);

  if (status !== LoadingStatus.SUCCESS) {
    return null;
  }

  const buttonClasses = cn(
    stylesCommon,
    // Default state
    "border-(--button-border) bg-(--button-bg) text-(--button-text)",
    // Interactive states
    "hover:border-(--button-border-hover) hover:bg-(--button-bg-hover) hover:text-(--button-text-hover)",
    "active:border-(--button-border-active) active:bg-(--button-bg-active) active:text-(--button-text-active)",
    "disabled:border-(--button-border-disabled) disabled:bg-(--button-bg-disabled) disabled:text-(--button-text-disabled)"
  );

  const buttonStyles = {
    // Default state
    "--button-text": colors.default.textColor,
    "--button-bg": colors.default.backgroundColor,
    "--button-border":
      colors.default.borderColor || colors.default.backgroundColor,

    // Hover state
    "--button-text-hover": colors.hover.textColor,
    "--button-bg-hover": colors.hover.backgroundColor,
    "--button-border-hover":
      colors.hover.borderColor || colors.hover.backgroundColor,

    // Active state
    "--button-text-active": colors.active.textColor,
    "--button-bg-active": colors.active.backgroundColor,
    "--button-border-active":
      colors.active.borderColor || colors.active.backgroundColor,

    // Disabled state
    "--button-text-disabled": colors.disabled.textColor,
    "--button-bg-disabled": colors.disabled.backgroundColor,
    "--button-border-disabled":
      colors.disabled.borderColor || colors.disabled.backgroundColor,
  } as React.CSSProperties;

  return (
    <Button
      className={buttonClasses}
      style={buttonStyles}
      as="button"
      variant="none"
      disabled={disabled}
      {...rest}
    >
      {children}
    </Button>
  );
}
